import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Sparkles, Send, Bot, User as UserIcon, History, Plus, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Reusable AI chat drawer.
 *  Pass:
 *    - endpoint: "/ai/patient-chat" or "/ai/doctor-chat"
 *    - title / description: shown in the sheet header
 *    - buttonLabel: label on the trigger button
 *    - extraPayload: object merged into every POST (e.g. { patient_id })
 *    - starterPrompts: array of 2-4 string prompts shown before first turn
 *    - testid: data-testid prefix for the trigger and sheet
 *    - historyEndpoints: optional { list, detail(session_id) } to enable the
 *      past-sessions side panel.
 */
export default function AiChatDrawer({
  endpoint,
  title,
  description,
  buttonLabel = "Ask Co-pilot",
  extraPayload = {},
  starterPrompts = [],
  testid = "ai-chat",
  historyEndpoints = null,
  caseSwitcher = null,  // { listEndpoint, labelKey, payloadKey, idKey, allLabel }
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [session, setSession] = useState(null);
  const [posting, setPosting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [cases, setCases] = useState([]);
  const [activeCase, setActiveCase] = useState("");  // "" = unscoped
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, posting]);

  const loadSessions = useCallback(async () => {
    if (!historyEndpoints?.list) return;
    try {
      const { data } = await api.get(historyEndpoints.list);
      setSessions(data.sessions || []);
      setSessionsLoaded(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [historyEndpoints]);

  useEffect(() => {
    if (open && historyEndpoints?.list && !sessionsLoaded) loadSessions();
  }, [open, historyEndpoints, sessionsLoaded, loadSessions]);

  useEffect(() => {
    if (!open || !caseSwitcher?.listEndpoint || cases.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get(caseSwitcher.listEndpoint);
        setCases(data[caseSwitcher.idKey === "id" ? "patients" : "items"] || data.patients || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open, caseSwitcher, cases.length]);

  const openSession = async (sid) => {
    if (!historyEndpoints?.detail) return;
    try {
      const { data } = await api.get(historyEndpoints.detail(sid));
      setMessages((data.messages || []).map((m) => ({ role: m.role, content: m.content })));
      setSession(sid);
      setShowHistory(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startNew = () => {
    setMessages([]);
    setSession(null);
    setShowHistory(false);
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || posting) return;
    setMessages((p) => [...p, { role: "user", content: trimmed }]);
    setInput("");
    setPosting(true);
    try {
      const payload = { message: trimmed, session_id: session, ...extraPayload };
      if (caseSwitcher?.payloadKey && activeCase) payload[caseSwitcher.payloadKey] = activeCase;
      const { data } = await api.post(endpoint, payload);
      if (!session) setSession(data.session_id);
      setMessages((p) => [...p, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((p) => [...p, { role: "assistant", content: "Sorry — I couldn't respond just now. Try again.", error: true }]);
    } finally {
      setPosting(false);
      // Refresh the sessions list so a brand-new session appears in history
      if (historyEndpoints?.list) setSessionsLoaded(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid={`${testid}-open`}>
        <Sparkles size={14} className="mr-1 text-primary" /> {buttonLabel}
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col h-full" data-testid={`${testid}-sheet`}>
          <SheetHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <SheetTitle className="font-serif text-primary text-xl flex items-center gap-2">
                  <Sparkles size={18} className="text-primary" /> {title}
                </SheetTitle>
                <SheetDescription>{description}</SheetDescription>
                {caseSwitcher && (
                  <div className="mt-3" data-testid={`${testid}-case-switcher-wrap`}>
                    <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Scope</label>
                    <select
                      value={activeCase}
                      onChange={(e) => { setActiveCase(e.target.value); startNew(); }}
                      className="mt-1 w-full h-8 rounded-md border border-input bg-background text-xs px-2"
                      data-testid={`${testid}-case-switcher`}
                    >
                      <option value="">{caseSwitcher.allLabel || "No specific patient"}</option>
                      {cases.map((c) => (
                        <option key={c[caseSwitcher.idKey]} value={c[caseSwitcher.idKey]}>
                          {c[caseSwitcher.labelKey] || c[caseSwitcher.idKey]}
                          {c.appointment_count ? ` · ${c.appointment_count} visits` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {historyEndpoints?.list && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={startNew}
                    title="New conversation"
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border text-muted-foreground hover:text-primary hover:border-primary"
                    data-testid={`${testid}-new`}
                  >
                    <Plus size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowHistory((s) => !s)}
                    title="Past conversations"
                    className={`h-8 w-8 inline-flex items-center justify-center rounded-md border ${showHistory ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:text-primary hover:border-primary"}`}
                    data-testid={`${testid}-history-toggle`}
                  >
                    <History size={14} />
                  </button>
                </div>
              )}
            </div>
          </SheetHeader>

          {showHistory && (
            <div className="border-b border-border -mx-6 px-6 py-3 bg-muted/20 max-h-72 overflow-y-auto" data-testid={`${testid}-history-panel`}>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Past conversations</div>
              {sessions.length === 0 ? (
                <div className="text-xs text-muted-foreground">No past conversations yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {sessions.map((s) => {
                    const active = s.session_id === session;
                    return (
                      <li key={s.session_id}>
                        <button
                          type="button"
                          onClick={() => openSession(s.session_id)}
                          className={`w-full text-left rounded-md border p-2.5 text-xs transition-colors ${active ? "border-primary bg-primary/5" : "border-border bg-white hover:border-primary/40"}`}
                          data-testid={`${testid}-history-item-${s.session_id}`}
                        >
                          <div className="flex items-center gap-1.5 text-primary">
                            <MessageSquare size={11} />
                            <span className="truncate flex-1">{s.preview}</span>
                            <span className="text-[10px] text-muted-foreground shrink-0">{s.message_count}</span>
                          </div>
                          <div className="mt-0.5 text-[10px] text-muted-foreground">
                            {s.last_at ? new Date(s.last_at).toLocaleString() : ""}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div ref={scrollerRef} className="flex-1 overflow-y-auto -mx-6 px-6 py-4 space-y-4" data-testid={`${testid}-scroller`}>
            {messages.length === 0 && starterPrompts.length > 0 && (
              <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Try asking</div>
                {starterPrompts.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(p)}
                    className="w-full text-left rounded-md border border-border bg-muted/30 p-3 text-sm text-primary hover:bg-muted/60"
                    data-testid={`${testid}-starter-${i}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="shrink-0 h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Bot size={14} />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-primary-foreground" :
                  m.error ? "bg-rose-50 text-rose-900 border border-rose-200" :
                  "bg-muted/40 text-primary border border-border"
                }`}>
                  {m.content}
                </div>
                {m.role === "user" && (
                  <div className="shrink-0 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <UserIcon size={14} />
                  </div>
                )}
              </div>
            ))}
            {posting && (
              <div className="flex gap-2 justify-start" data-testid={`${testid}-typing`}>
                <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Bot size={14} />
                </div>
                <div className="rounded-lg px-3 py-2 bg-muted/40 border border-border">
                  <div className="flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce" />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "240ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border pt-3 -mx-6 px-6">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                rows={2}
                placeholder="Type your question — Enter to send, Shift+Enter for a new line"
                className="flex-1 resize-none"
                data-testid={`${testid}-input`}
              />
              <Button onClick={() => send()} disabled={posting || !input.trim()} data-testid={`${testid}-send`}>
                <Send size={14} />
              </Button>
            </div>
            <div className="mt-2 text-[10px] text-muted-foreground">
              AI replies are grounded on your own data. Not a diagnosis — always consult a licensed clinician.
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
